import { useMemo } from 'react';
import moment from 'moment-timezone';
import useSWR from 'swr';

import { fetcher } from 'api/utils';
import { METABASE_API_ROOT } from 'appConstants';
import useAnalyticsContext from 'containers/Analytics/useAnalyticsContext';
import { useTenant, useLocalization } from 'hooks';
import API from 'utils/request';

const useMetabase = (questionId: number, dataParams, options = { needDateRange: true }) => {
  const { name: tenantName } = useTenant();
  const { dateRange, isDemoMode } = useAnalyticsContext();
  const { timezone } = useLocalization();
  const { needDateRange } = options;

  const shouldFetchToken = Boolean(!isDemoMode && tenantName && questionId);

  const tokenKey = shouldFetchToken
    ? {
        url: '/v4/dash/report_token',
        data: { type: 'question', id: questionId, params: { timezone } }
      }
    : null;
  const {
    data: tokenRes,
    error: tokenError,
    isLoading: isTokenValidating,
    mutate: tokenRevalidate
  } = useSWR(tokenKey, ({ url, data }: { url: string; data: Record<string, any> }) =>
    API.post(url, data).then(res => res.data)
  );

  const params = useMemo(() => {
    if (!needDateRange) return dataParams || {};

    const startDate = moment.tz(dateRange[0], timezone).startOf('day').utc();
    const endDate = moment.tz(dateRange[1], timezone).add(1, 'day').startOf('day').utc();
    const diffDays = endDate.diff(startDate, 'days');

    const prevStart = moment(startDate).subtract(diffDays, 'days');
    const prevEnd = startDate;

    return {
      start_date: startDate.format('YYYY-MM-DD HH:mm:ss'),
      end_date: endDate.format('YYYY-MM-DD HH:mm:ss'),
      previous_start_date: prevStart.format('YYYY-MM-DD HH:mm:ss'),
      previous_end_date: prevEnd.format('YYYY-MM-DD HH:mm:ss'),
      ...(dataParams || {})
    };
  }, [dateRange, dataParams, timezone, needDateRange]);

  const dataKey = tokenRes?.token
    ? { url: `${METABASE_API_ROOT}/api/embed/card/${tokenRes.token}/query`, params }
    : null;
  const {
    data,
    error,
    isLoading: isDataValidating,
    mutate: dataRevalidate
  } = useSWR(dataKey, async key => {
    const response = await fetcher(key);
    if (response === 'An error occurred while running the query.') {
      return { error: 'Metabase Error' };
    }
    return response;
  });

  return {
    data: data ?? null,
    token: tokenRes?.token,
    dataError: error || data?.error,
    tokenError,
    tokenRevalidate,
    dataRevalidate,
    isDataValidating,
    isTokenValidating
  };
};

export default useMetabase;
